import { useNavigate, Link } from "react-router-dom";

const COLUMNS = {
  tools: {
    title: "Find Deals",
    links: [
      { label: "Top Deals Today", to: "/top-deals" },
      { label: "Best Deals Under $300K", to: "/best-deals-under-300k" },
      { label: "Top Multifamily Deals", to: "/top-multifamily-deals" },
      { label: "Cash Flow Calculator", to: "/cash-flow-calculator-real-estate" },
    ],
  },
  learn: {
    title: "Learn",
    links: [
      { label: "How Scoring Works", to: "/how-scoring-works" },
      { label: "How to Analyze Rentals", to: "/how-to-analyze-rental-properties" },
      { label: "How We Calculate Cash Flow", to: "/faq/how-realixdata-calculates-cash-flow" },
      { label: "What Is a Good Cap Rate?", to: "/what-is-a-good-cap-rate" },
      { label: "The 1% Rule", to: "/1-percent-rule-real-estate" },
      { label: "All FAQ & Guides", to: "/faq" },
    ],
  },
  markets: {
    title: "Markets",
    links: [
      { label: "Best Rental Markets 2026", to: "/best-rental-markets-tampa-jacksonville-atlanta" },
      { label: "25 Markets We Analyze", to: "/faq/markets-we-analyze" },
    ],
  },
  company: {
    title: "Company",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
  legal: {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/legal/privacy" },
      { label: "Terms of Service", to: "/legal/terms" },
      { label: "Investment Disclaimer", to: "/legal/investment-disclaimer" },
    ],
  },
};

// Redesigned public footer grouping. Every link from COLUMNS is preserved:
// 4 high-value links per column + the rest in a crawlable "Explore more" row.
const DARK_COLUMNS = {
  product: {
    title: "Product",
    links: [
      { label: "Find Deals", to: "/top-deals" },
      { label: "Analyze Property", to: "/dashboard" },
      { label: "Deal Score", to: "/how-scoring-works" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  resources: {
    title: "Resources",
    links: [
      { label: "Investment Guides", to: "/how-to-analyze-rental-properties" },
      { label: "Cash Flow Calculator", to: "/cash-flow-calculator-real-estate" },
      { label: "Best Rental Markets", to: "/best-rental-markets-tampa-jacksonville-atlanta" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  company: {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Realtor Reports", to: "/create-report" },
    ],
  },
  legal: {
    title: "Legal",
    links: [
      { label: "Privacy", to: "/legal/privacy" },
      { label: "Terms", to: "/legal/terms" },
      { label: "Investment Disclaimer", to: "/legal/investment-disclaimer" },
    ],
  },
};

const DARK_MORE_LINKS = [
  { label: "Best Deals Under $300K", to: "/best-deals-under-300k" },
  { label: "Top Multifamily Deals", to: "/top-multifamily-deals" },
  { label: "25 Markets We Analyze", to: "/faq/markets-we-analyze" },
  { label: "How We Calculate Cash Flow", to: "/faq/how-realixdata-calculates-cash-flow" },
  { label: "What Is a Good Cap Rate?", to: "/what-is-a-good-cap-rate" },
  { label: "The 1% Rule", to: "/1-percent-rule-real-estate" },
];

const slug = (s) => s.toLowerCase().replace(/[\s/&,?]+/g, "-").replace(/-+$/, "");

function DarkFooter() {
  return (
    <footer className="bg-rd-navy text-white border-t border-white/10 pb-20 md:pb-0" data-testid="site-footer">
      <div className="rd-container py-14 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-[1.2fr_repeat(4,1fr)] gap-x-6 gap-y-10">
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2.5" aria-label="RealixData home">
              <img src="/logo.png" alt="RealixData" className="h-10 w-auto" />
              <span className="text-lg font-extrabold tracking-tight">RealixData</span>
            </Link>
            <p className="mt-4 text-sm text-white/55 leading-relaxed max-w-xs">Real estate investment analysis. Cash flow, cap rate, ROI and the Deal Score for any property in seconds.</p>
          </div>
          {Object.entries(DARK_COLUMNS).map(([key, col]) => (
            <nav key={key} aria-label={col.title}>
              <p className="rd-eyebrow text-white/45 mb-4">{col.title}</p>
              <ul className="flex flex-col gap-1">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={l.to} className="inline-flex items-center min-h-[36px] text-sm text-white/75 hover:text-white transition-colors" data-testid={`footer-${key}-${slug(l.label)}`}>{l.label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <nav className="mt-10 pt-6 border-t border-white/10" aria-label="Explore more" data-testid="footer-more">
          <p className="rd-eyebrow text-white/35 mb-3">Explore more</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-1">
            {DARK_MORE_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="inline-flex items-center min-h-[36px] text-sm text-white/55 hover:text-white transition-colors" data-testid={`footer-more-${slug(l.label)}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/45">
          <p>&copy; {new Date().getFullYear()} RealixData. All rights reserved.</p>
          <p>Analysis is for informational purposes only and is not investment advice.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Footer({ variant = "default" }) {
  const navigate = useNavigate();

  if (variant === "dark") return <DarkFooter />;

  const handleClick = (to) => {
    if (to.startsWith("/#")) {
      const el = document.getElementById(to.replace("/#", ""));
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    navigate(to);
  };

  return (
    <footer className="border-t border-border bg-card/50 py-10 px-4 sm:px-6" data-testid="site-footer">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8 mb-8">
          {Object.entries(COLUMNS).map(([key, col]) => (
            <div key={key}>
              <p className="text-[11px] font-semibold text-foreground uppercase tracking-wider mb-3">{col.title}</p>
              <div className="flex flex-col gap-1.5">
                {col.links.map((link) => (
                  <button
                    key={link.to}
                    onClick={() => handleClick(link.to)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left leading-relaxed"
                    data-testid={`footer-${key}-${link.label.toLowerCase().replace(/[\s/&,]+/g, '-')}`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between pt-6 border-t border-border gap-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.png" alt="RealixData" className="h-7 w-auto opacity-60" />
            <span className="text-sm font-semibold text-muted-foreground">RealixData</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            &copy; {new Date().getFullYear()} RealixData. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
